import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// =============================================================================
// TIER GUARD — helpers
// Checks tier boundaries before Horizon fires cart/change, swaps variant if needed.
// Tier labels must match your Shopify product option1 values exactly.
// =============================================================================
const TIER_CONFIG = [
  { min: 50, max: null, label: '50+'      },
  { min: 10, max: 49,  label: '10 to 49' },
  { min: 1,  max: 9,   label: '1 to 9'   },
];

function tierParseTierRange(text) {
  if (!text) return null;
  const t = String(text).toLowerCase().trim();
  const plusMatch = t.match(/(\d+)\s*\+/);
  if (plusMatch) return { min: Number(plusMatch[1]), max: null };
  const dashMatch = t.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (dashMatch) return { min: Number(dashMatch[1]), max: Number(dashMatch[2]) };
  const toMatch = t.match(/(\d+)\s*to\s*(\d+)/);
  if (toMatch) return { min: Number(toMatch[1]), max: Number(toMatch[2]) };
  return null;
}

function tierDesiredTier(q) {
  for (const tier of TIER_CONFIG) {
    if (q >= tier.min && (tier.max === null || q <= tier.max)) return tier;
  }
  return TIER_CONFIG[TIER_CONFIG.length - 1];
}

function tierSameRange(a, b) {
  if (!a || !b) return false;
  return a.min === b.min && (a.max ?? null) === (b.max ?? null);
}

function tierNorm(s) { return String(s ?? '').trim().toLowerCase(); }

const tierProductCache = new Map();

async function tierGetProduct(handle) {
  const cached = tierProductCache.get(handle);
  if (cached && Date.now() - cached.ts < 5 * 60 * 1000) return cached.data;
  try {
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    tierProductCache.set(handle, { data, ts: Date.now() });
    return data;
  } catch { return null; }
}

function tierFindVariant(product, wantedRange, size, colour) {
  if (!product?.variants?.length) return null;
  for (const v of product.variants) {
    const r = tierParseTierRange(v.option1);
    if (!tierSameRange(r, wantedRange)) continue;
    if (tierNorm(v.option2) !== tierNorm(size)) continue;
    if (product.options.length >= 3 && tierNorm(v.option3) !== tierNorm(colour)) continue;
    return v;
  }
  return null;
}
// =============================================================================
// END TIER GUARD helpers
// =============================================================================

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * Patched with tier guard: checks if the new quantity crosses a tier boundary
   * and swaps the variant before Horizon fires its own cart/change request.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    // ── TIER GUARD ────────────────────────────────────────────────────────────
    // Only check tier on real quantity changes (not removals)
    if (quantity > 0) {
      try {
        const cartRes = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart    = await cartRes.json();
        const item    = cart?.items?.[line - 1];

        if (item) {
          const tierVal      = item?.options_with_values?.[0]?.value || '';
          const currentRange = tierParseTierRange(tierVal);

          if (currentRange) {
            const wanted = tierDesiredTier(quantity);

            if (!tierSameRange(currentRange, wanted)) {
              // Tier boundary crossed — find the correct sibling variant
              const size    = item?.options_with_values?.[1]?.value || '';
              const colour  = item?.options_with_values?.[2]?.value || '';
              const product = await tierGetProduct(item.handle);
              const targetV = product ? tierFindVariant(product, wanted, size, colour) : null;

              if (targetV) {
                // Check if target variant already exists as another cart line
                const existingIdx = cart.items.findIndex(
                  (it, idx) => idx !== line - 1 && it.variant_id === targetV.id
                );

                if (existingIdx !== -1) {
                  // Target tier already in cart — merge qty into it
                  const mergedQty = Number(cart.items[existingIdx].quantity) + quantity;
                  await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ id: cart.items[existingIdx].key, quantity: mergedQty }),
                  });
                } else {
                  // No existing line for target tier — add it fresh
                  await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({
                      id: targetV.id,
                      quantity,
                      ...(item.properties && Object.keys(item.properties).length
                        ? { properties: item.properties } : {}),
                      ...(item.selling_plan_allocation?.selling_plan?.id
                        ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}),
                    }),
                  });
                }

                // Now remove the old incorrect-tier line by its stable key.
                // We do this ourselves and return early so Horizon doesn't also
                // fire a cart/change — that was causing the item to vanish.
                const sectionsToUpdate = new Set([this.sectionId]);
                document.querySelectorAll('cart-items-component').forEach((el) => {
                  if (el instanceof HTMLElement && el.dataset.sectionId) {
                    sectionsToUpdate.add(el.dataset.sectionId);
                  }
                });

                cartTotal?.shimmer();

                const removeBody = JSON.stringify({
                  id: item.key,
                  quantity: 0,
                  sections: Array.from(sectionsToUpdate).join(','),
                  sections_url: window.location.pathname,
                });

                // Use cart/change with the item key (stable) to remove the old line,
                // then let Horizon re-render using the sections response — same as
                // its own updateQuantity flow, so the drawer refreshes correctly.
                fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: removeBody }))
                  .then((response) => response.text())
                  .then((responseText) => {
                    const parsedResponseText = JSON.parse(responseText);
                    resetShimmer(this);
                    if (parsedResponseText.errors) return;

                    const newSectionHTML = new DOMParser().parseFromString(
                      parsedResponseText.sections[this.sectionId],
                      'text/html'
                    );
                    const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
                    const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

                    this.#updateQuantitySelectors(parsedResponseText);

                    this.dispatchEvent(
                      new CartUpdateEvent(parsedResponseText, this.sectionId, {
                        itemCount: newCartItemCount,
                        source: 'cart-items-component',
                        sections: parsedResponseText.sections,
                      })
                    );

                    morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
                    this.#updateCartQuantitySelectorButtonStates();
                  })
                  .catch((error) => { console.error(error); })
                  .finally(() => {
                    this.#enableCartItems();
                    cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
                  });

                // Return early — we handled the full cart update above,
                // so Horizon's normal flow below should not run.
                return;
              }
              // targetV not found — fall through to Horizon's normal flow.
              // tier-cart-guard.js will block checkout as a safety net.
            }
          }
        }
      } catch {
        // If anything goes wrong, fall through to Horizon's normal flow.
      }
    }
    // ── END TIER GUARD ────────────────────────────────────────────────────────

    // Normal Horizon flow (no tier change needed, or tier guard fell through)
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}